import { FetchResult } from 'apollo-boost';
import { Epic } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { Error } from 'tslint/lib/error';

import { Action } from '../actions';
import {
    TOGGLE_FEED_FILTER_PRIORITY_MODAL,
    TOGGLE_FEED_FILTER_RATE_MODAL,
    toggleFeedFilterPriorityModal,
    toggleFeedFilterRateModal,
    UPDATE_FEED_FILTER_PRIORITY_CELL,
    UPDATE_FEED_FILTER_RATE_CELL,
    updateFeedFilterCellResult,
    UpdateFeedFilterCellResultAction,
    UpdateFeedFilterPriorityCellAction,
    UpdateFeedFilterRateCellAction
} from '../actions/feed-filters/grid';
import {
    changeFeedFiltersAutobudgetPriority,
    UPDATE_FEED_FILTERS_AUTOBUDGET_PRIORITY,
    UpdateFeedFiltersAutobudgetPriorityAction,
    updateFeedFiltersAutobudgetPriorityResult
} from '../actions/feed-filters/priority-form';
import {
    changeFeedFiltersRate,
    setFeedFilterRateFormErrors,
    SetFeedFilterRateFormErrorsAction,
    UPDATE_FEED_FILTERS_RATE,
    UpdateFeedFiltersRateAction,
    updateFeedFiltersRateResult
} from '../actions/feed-filters/rate-form';
import {
    deleteEditCellData,
    DeteleEditCellData,
    resetGridData,
    UpdateEditCellData,
    updateEditCellData
} from '../actions/grid-screen';
import { mapFeedFilterRateToPrice, mapGridFeedFilterPricesToApiFormat } from '../mappers/feedFilters';
import { getFiltersSaveValidationResult } from '../mappers/group/group';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { ApiMethodExecutingError } from '../service/Errors';
import {
    showFeedFiltersUpdateErrorMessage,
    showFeedFiltersUpdateSuccessMessage
} from '../service/FeedFiltersHelpers/FeedFiltersHelpers';
import {
    getRateFormErrors,
    mapValidationResultItemsToFieldsFilter
} from '../service/GroupFiltersErrors/GroupFiltersErrors';
import { EpicDeps } from '../service/StoreFactory';
import { CellKeyFields, PageStatus, ValidationErrorData } from '../typings/common';
import { DnaEpic, DnaEpicAction } from '../typings/epic';
import { FeedFilterPricesInfo } from '../typings/feedFilters';
import {
    GdShowConditionAutobudgetPriority,
    GdShowConditionAutobudgetPriorityInput,
    UpdateSmartFilters
} from '../typings/graphql-grid';
import { FeedFiltersAction } from '../typings/grid';
import { State } from '../typings/state';

type UpdateFeedFilterCellEpicObservable = Observable<
    DeteleEditCellData | UpdateFeedFilterCellResultAction | SetFeedFilterRateFormErrorsAction | UpdateEditCellData
>;

const validateRateForm = (result: FetchResult<UpdateSmartFilters>): Array<ValidationErrorData> => {
    const rawValidationErrors = getFiltersSaveValidationResult(result);
    if (rawValidationErrors.length === 0) {
        return [];
    }

    const validationErrors = mapValidationResultItemsToFieldsFilter(rawValidationErrors);

    return getRateFormErrors(validationErrors);
};

const saveFeedFilterCell = ({
    feedFilter,
    cellKey,
    login,
    deps,
    actionType
}: {
    feedFilter: FeedFilterPricesInfo;
    cellKey: CellKeyFields;
    login: string;
    deps: EpicDeps;
    actionType: FeedFiltersAction;
}) => {
    const resultFeedFilter = mapGridFeedFilterPricesToApiFormat(feedFilter);

    return from(deps.smartFiltersMutationExecutor.updateSmartFilters({ updateItems: [resultFeedFilter] }, login)).pipe(
        mergeMap(
            (result: FetchResult<UpdateSmartFilters>): UpdateFeedFilterCellEpicObservable => {
                const validationErrors = validateRateForm(result);

                if (validationErrors.length === 0) {
                    if (result.data) {
                        showFeedFiltersUpdateSuccessMessage(result.data, actionType);
                    }

                    return from([deleteEditCellData(cellKey), updateFeedFilterCellResult(feedFilter)]);
                }

                return from([
                    updateEditCellData(cellKey, {
                        isSaving: false,
                        error: 'error'
                    }),
                    setFeedFilterRateFormErrors(validationErrors)
                ]);
            }
        ),
        catchError(error => {
            showFeedFiltersUpdateErrorMessage(error);

            return of(
                updateEditCellData(cellKey, {
                    isSaving: false,
                    error: error.getMessage()
                })
            );
        })
    );
};

export const saveFeedFiltersAutobudgetPriorityEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_FEED_FILTERS_AUTOBUDGET_PRIORITY).pipe(
        mergeMap(
            (action: UpdateFeedFiltersAutobudgetPriorityAction): Observable<Action> => {
                const {
                    commonData,
                    feedFilters: { priorityForm }
                } = store$.value;
                const { login } = commonData;
                const { filtersIds } = action;

                const updateItems = filtersIds.map(id => ({
                    id,
                    autobudgetPriority: GdShowConditionAutobudgetPriorityInput[priorityForm.autobudgetPriority]
                }));

                return from(deps.smartFiltersMutationExecutor.updateSmartFilters({ updateItems }, login)).pipe(
                    mergeMap(
                        (result: FetchResult<UpdateSmartFilters>): Observable<Action> => {
                            if (result.data) {
                                showFeedFiltersUpdateSuccessMessage(result.data, FeedFiltersAction.changeAutobudget);
                            }

                            return of(
                                updateFeedFiltersAutobudgetPriorityResult(PageStatus.LOADED),
                                toggleFeedFilterPriorityModal(),
                                resetGridData()
                            );
                        }
                    ),
                    catchError(
                        (error: ApiMethodExecutingError | Error): Observable<Action> => {
                            showFeedFiltersUpdateErrorMessage(error);

                            return of(updateFeedFiltersAutobudgetPriorityResult(PageStatus.ERROR));
                        }
                    )
                );
            }
        )
    );
};

export const saveFeedFiltersRateEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_FEED_FILTERS_RATE).pipe(
        mergeMap(
            (action: UpdateFeedFiltersRateAction): Observable<Action> => {
                const {
                    commonData: { login },
                    feedFilters: { rateForm }
                } = store$.value;
                const { rateType, rate } = rateForm;
                const { filtersIds } = action;

                const resultFilterPrice = mapFeedFilterRateToPrice(rateType, rate);

                const updateItems = filtersIds.map(id => ({
                    id,
                    ...resultFilterPrice
                }));

                return from(deps.smartFiltersMutationExecutor.updateSmartFilters({ updateItems }, login)).pipe(
                    mergeMap(
                        (result: FetchResult<UpdateSmartFilters>): Observable<Action> => {
                            const validationErrors = validateRateForm(result);

                            if (validationErrors.length !== 0) {
                                return of(setFeedFilterRateFormErrors(validationErrors));
                            }

                            if (result.data) {
                                showFeedFiltersUpdateSuccessMessage(result.data, FeedFiltersAction.changeRate);
                            }

                            return from([
                                updateFeedFiltersRateResult(PageStatus.LOADED),
                                toggleFeedFilterRateModal(),
                                resetGridData()
                            ]);
                        }
                    ),
                    catchError(
                        (error: ApiMethodExecutingError | Error): Observable<Action> => {
                            showFeedFiltersUpdateErrorMessage(error);

                            return of(updateFeedFiltersRateResult(PageStatus.ERROR));
                        }
                    )
                );
            }
        )
    );
};

export const saveFeedFilterRateCellEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_FEED_FILTER_RATE_CELL).pipe(
        mergeMap((action: UpdateFeedFilterRateCellAction) => {
            const { login, view } = selectGridSourceParams(store$.value);
            const {
                feedFilters: { rateForm }
            } = store$.value;

            const { cellKeyPartial, rateType } = action;
            const feedFilter = {
                ...mapFeedFilterRateToPrice(rateType, rateForm.rate),
                id: cellKeyPartial.id
            };

            const cellKey = {
                ...cellKeyPartial,
                view
            };

            return saveFeedFilterCell({
                feedFilter,
                cellKey,
                login,
                deps,
                actionType: FeedFiltersAction.changeRate
            });
        })
    );
};

export const saveFeedFilterPriorityCellEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_FEED_FILTER_PRIORITY_CELL).pipe(
        mergeMap((action: UpdateFeedFilterPriorityCellAction) => {
            const { login, view } = selectGridSourceParams(store$.value);
            const {
                feedFilters: { priorityForm }
            } = store$.value;

            const { cellKeyPartial } = action;
            const { autobudgetPriority } = priorityForm;
            const feedFilter = {
                autobudgetPriority,
                id: cellKeyPartial.id
            };

            const cellKey = {
                ...cellKeyPartial,
                view
            };

            return saveFeedFilterCell({
                feedFilter,
                cellKey,
                login,
                deps,
                actionType: FeedFiltersAction.changeAutobudget
            });
        })
    );
};

export const setInitialFeedFilterRateEpic: DnaEpic = (action$, store$) => {
    return action$.ofType(TOGGLE_FEED_FILTER_RATE_MODAL).pipe(
        mergeMap(
            (): DnaEpicAction => {
                const {
                    grid: {
                        feedFilters: { isRateModalOpen }
                    }
                } = store$.value;

                if (isRateModalOpen) {
                    return of(changeFeedFiltersRate({ rate: 0 }));
                }

                return EMPTY;
            }
        )
    );
};

export const setInitialFeedFilterPriorityEpic: DnaEpic = (action$, store$) => {
    return action$.ofType(TOGGLE_FEED_FILTER_PRIORITY_MODAL).pipe(
        mergeMap(
            (): DnaEpicAction => {
                const {
                    grid: {
                        feedFilters: { isPriorityModalOpen }
                    }
                } = store$.value;

                if (isPriorityModalOpen) {
                    return of(changeFeedFiltersAutobudgetPriority(GdShowConditionAutobudgetPriority.MEDIUM));
                }

                return EMPTY;
            }
        )
    );
};
